import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XCircle, RotateCcw, Home, Mail, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BTN_OUTLINE, BTN_PRIMARY } from '../config/brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEAD_STORAGE_KEY = 'digirobot_lead_id';

const ReserverCancel = () => {
  const location = useLocation();
  const bookingId = new URLSearchParams(location.search).get('booking_id');
  const [ready, setReady] = useState(!bookingId);

  useEffect(() => {
    if (!bookingId) return;

    let cancelled = false;
    const restorePending = async () => {
      try {
        sessionStorage.setItem(LEAD_STORAGE_KEY, bookingId);
        await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/cancel`, { method: 'POST' });
      } catch {
        // non bloquant — la reprise front fonctionne quand même via sessionStorage
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    restorePending();
    return () => { cancelled = true; };
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-orange-50/30">
      <Navbar />
      <div className="pt-below-site-header pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <p className="text-center text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-4">
            DigiRobot
          </p>
          <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#fab03a] to-[#f97316]" aria-hidden />
            <div className="py-11 px-6 sm:px-10 text-center animate-in fade-in slide-in-from-bottom-3 duration-500">
              {!ready ? (
                <div role="status" aria-live="polite">
                  <Loader2 className="h-8 w-8 text-[#f97316] animate-spin mx-auto mb-4" aria-hidden />
                  <p className="text-slate-600 text-sm">Enregistrement de votre sélection…</p>
                </div>
              ) : (
                <>
                  <div className="w-[4.5rem] h-[4.5rem] rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-5">
                    <XCircle className="h-10 w-10 text-orange-500" aria-hidden />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 tracking-tight">
                    Paiement annulé
                  </h1>
                  <p className="text-slate-600 mb-2 leading-relaxed">
                    Votre sélection a bien été enregistrée, mais le paiement n&apos;a pas abouti.
                  </p>
                  <p className="text-slate-500 text-sm mb-6">
                    Reprenez quand vous le souhaitez — vos informations restent disponibles.
                  </p>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 mb-7 text-left">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[rgba(58,0,224,0.08)] flex items-center justify-center shrink-0">
                        <CreditCard className="h-4 w-4 text-[#3a00e0]" aria-hidden />
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed pt-1">
                        Aucun montant n&apos;a été débité. Retournez sur la réservation pour finaliser l&apos;inscription.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button asChild className={`h-11 ${BTN_PRIMARY}`}>
                      <Link to="/reserver">
                        <RotateCcw className="h-4 w-4 mr-2" />Reprendre la réservation
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className={`h-11 ${BTN_OUTLINE}`}>
                      <Link to="/"><Home className="h-4 w-4 mr-2" />Accueil</Link>
                    </Button>
                  </div>
                  <Button asChild variant="link" className="mt-4 text-slate-500 hover:text-[#3a00e0]">
                    <Link to="/contact"><Mail className="h-4 w-4 mr-1" />Une question ? Contactez-nous</Link>
                  </Button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReserverCancel;
